'use client'

/**
 * Post-checkout celebration. Stripe's hosted checkout redirects a new paid
 * signup back to `/?welcome=1&stripe=success` (see
 * src/app/api/checkout/stripe/signup/route.ts). When those params are present
 * we fire a one-shot confetti burst and a congrats modal telling them to
 * confirm their email — the account isn't usable until they click the
 * confirmation link (hard-gated in /api/auth/login).
 *
 * Reads the query string from `window` (not useSearchParams) so no Suspense
 * boundary is needed, and clears the params on close so a refresh/back doesn't
 * re-trigger it. Confetti is a dependency-free canvas burst.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'

const CONFETTI_COLORS = ['#3b82f6', '#a855f7', '#22c55e', '#facc15', '#fb7185', '#22d3ee', '#f472b6', '#a3e635', '#fb923c']

/** One-shot confetti burst on a full-viewport canvas. Returns a cancel fn. */
function fireConfetti(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d')
  if (!ctx) return () => {}
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const W = (canvas.width = window.innerWidth * dpr)
  const H = (canvas.height = window.innerHeight * dpr)
  canvas.style.width = `${window.innerWidth}px`
  canvas.style.height = `${window.innerHeight}px`

  // Two launch points (lower-left and lower-right) firing up and inward.
  const parts = Array.from({ length: 380 }, (_, i) => {
    const left = i % 2 === 0
    return {
      x: (left ? 0.1 : 0.9) * W,
      y: H * 0.92,
      vx: ((left ? 1 : -1) * (Math.random() * 9 + 3) + (Math.random() - 0.5) * 6) * dpr,
      vy: -(Math.random() * 18 + 12) * dpr,
      size: (Math.random() * 12 + 8) * dpr,
      color: CONFETTI_COLORS[(Math.random() * CONFETTI_COLORS.length) | 0],
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.4,
    }
  })

  const gravity = 0.34 * dpr
  const durationMs = 3400
  const start = performance.now()
  let raf = 0

  const tick = (now: number) => {
    const elapsed = now - start
    ctx.clearRect(0, 0, W, H)
    // Hold full brightness for the first 65%, then fade the tail — keeps the
    // colors vivid rather than washing out immediately.
    const holdMs = durationMs * 0.65
    const fade = elapsed < holdMs ? 1 : Math.max(0, 1 - (elapsed - holdMs) / (durationMs - holdMs))
    for (const p of parts) {
      p.vy += gravity
      p.vx *= 0.995
      p.x += p.vx
      p.y += p.vy
      p.rot += p.vr
      ctx.save()
      ctx.globalAlpha = fade
      ctx.translate(p.x, p.y)
      ctx.rotate(p.rot)
      ctx.fillStyle = p.color
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.5)
      ctx.restore()
    }
    if (elapsed < durationMs) raf = requestAnimationFrame(tick)
    else ctx.clearRect(0, 0, W, H)
  }
  raf = requestAnimationFrame(tick)
  return () => cancelAnimationFrame(raf)
}

/**
 * `title`/`body` come from site_settings (Settings → Subscriptions →
 * "Payment Success Dialog"), passed by the homepage. They fall back to the
 * i18n defaults when the settings are empty/unset.
 */
export function StripeWelcomeModal({ title, body }: { title?: string; body?: string } = {}) {
  const t = useTranslations('home.stripeWelcome')
  const [open, setOpen] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Detect the post-checkout params on mount (client only).
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot read of the post-checkout params from window.location after hydration
      if (p.get('welcome') === '1' && p.get('stripe') === 'success') setOpen(true)
    } catch {
      /* no-op */
    }
  }, [])

  // Fire confetti once the modal opens.
  useEffect(() => {
    if (!open || !canvasRef.current) return
    return fireConfetti(canvasRef.current)
  }, [open])

  const close = useCallback(() => {
    setOpen(false)
    // Strip the query params so a refresh/back-nav doesn't re-trigger.
    try {
      window.history.replaceState({}, '', window.location.pathname)
    } catch {
      /* no-op */
    }
  }, [])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  if (!open) return null

  return (
    <>
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{ position: 'fixed', inset: 0, zIndex: 10000, pointerEvents: 'none' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stripe-welcome-title"
        onClick={close}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(12,10,9,0.55)',
          padding: 16,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-900"
          style={{
            maxWidth: 440,
            width: '100%',
            padding: '36px 28px',
            textAlign: 'center',
            boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
          }}
        >
          <div style={{ fontSize: 52, lineHeight: 1 }} aria-hidden>🎉</div>
          <h2
            id="stripe-welcome-title"
            className="mt-4 text-2xl font-bold text-gray-900 dark:text-white"
          >
            {title?.trim() || t('title')}
          </h2>
          <p className="mt-3 whitespace-pre-line text-gray-600 dark:text-gray-300">{body?.trim() || t('body')}</p>
          <div className="mt-7">
            <Button
              onClick={close}
              size="lg"
              className="w-full bg-brand-700 font-semibold text-white hover:bg-brand-800"
            >
              {t('cta')}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
